<template>
  <a-visibility show :from="visibleFrom" class="breadcrumbs__wrapper">
    <div class="breadcrumbs__list-and-slot-wrapper">
      <div class="breadcrumbs__list">
        <div class="breadcrumbs__item text-caption">
          <a-link :to="ROUTE.HOMEPAGE">
            FM Home
          </a-link>
        </div>
        <div
          class="breadcrumbs__item text-caption"
          v-for="item in breadcrumbsWithDynamicNames"
          :key="item.routeName"
        >
          <span class="breadcrumbs__divider text-caption">></span>
          <span
            v-if="isActiveItem(item)"
            class="breadcrumbs__item-last text-caption"
          >
            {{ prettifyName(item.name) }}
          </span>
          <a-link
            v-else
            class="breadcrumbs__item-link text-caption"
            :to="generateRoute(item)"
          >
            {{ prettifyName(item.name) }}
          </a-link>
        </div>
      </div>
      <div class="breadcrumbs__slot-wrapper">
        <slot />
      </div>
    </div>
  </a-visibility>
</template>

<script>
import { mapGetters } from 'vuex'
import { propValidator, PROP_TYPES } from '@/utils/validators'
import { ROUTE } from 'enums/routes'
import { capitalize } from '@/plugins/helper'
import scss from '@/utils/scss'

const { breakpoints } = scss

export const BREADCRUMB_NAMING = {
  LOWERCASE_WITH_FIRST_CAPITAL: 'lowercase-with-first-capital',
  CAPITALIZE_EACH_WORD: 'capitalize-each-word',
  KEEP_AS_IS: 'keep-as-is'
}

export const BREADCRUMB_NAME_GENERATOR = {
  LOWERCASE_WITH_FIRST_CAPITAL: name => capitalize(name.toLowerCase()),
  CAPITALIZE_EACH_WORD: name =>
    name
      .split(' ')
      .map(v => capitalize(v.toLowerCase()))
      .join(' '),
  KEEP_AS_IS: name => name
}

const BREADCRUMB_NAME_PRETTIFIER_BY_BREADCRUMB_NAMING = {
  [BREADCRUMB_NAMING.LOWERCASE_WITH_FIRST_CAPITAL]:
    BREADCRUMB_NAME_GENERATOR.LOWERCASE_WITH_FIRST_CAPITAL,
  [BREADCRUMB_NAMING.CAPITALIZE_EACH_WORD]:
    BREADCRUMB_NAME_GENERATOR.CAPITALIZE_EACH_WORD,
  [BREADCRUMB_NAMING.KEEP_AS_IS]: BREADCRUMB_NAME_GENERATOR.KEEP_AS_IS
}

export default {
  name: 'ABreadcrumbs',
  props: {
    breadcrumbsDynamicNames: propValidator([PROP_TYPES.OBJECT], false, null),
    breadcrumbsNaming: propValidator(
      [PROP_TYPES.STRING],
      false,
      BREADCRUMB_NAMING.LOWERCASE_WITH_FIRST_CAPITAL,
      naming => Object.values(BREADCRUMB_NAMING).includes(naming)
    ),
    visibleFrom: propValidator(
      [PROP_TYPES.NUMBER],
      false,
      breakpoints.desktopMd
    )
  },
  data() {
    return {
      ROUTE
    }
  },
  computed: {
    ...mapGetters({
      breadcrumbs: 'breadcrumbs/breadcrumbs'
    }),
    breadcrumbsWithDynamicNames() {
      return this.breadcrumbsDynamicNames
        ? this.getNewNameForDynamicBreadcrumbs(
            this.breadcrumbs,
            this.breadcrumbsDynamicNames
          )
        : this.breadcrumbs
    }
  },
  methods: {
    isActiveItem(item) {
      return item.routeName === this.$route.name
    },
    generateRoute(data) {
      const route = { name: data.routeName }
      if (data.params) {
        route.params = data.params
      }
      return route
    },
    prettifyName(name = '') {
      return BREADCRUMB_NAME_PRETTIFIER_BY_BREADCRUMB_NAMING[
        this.breadcrumbsNaming
      ](name)
    },
    getNewNameForDynamicBreadcrumbs(breadcrumbs, breadcrumbsDynamicNames) {
      return breadcrumbs.map(breadcrumb => {
        const routeNameWithoutOptionalSymbol = this.getRouteNameWithoutOptionalSymbol(
          breadcrumb.routeName
        )

        const dynamicNameForCurrentBreadcrumb =
          breadcrumbsDynamicNames[routeNameWithoutOptionalSymbol]

        return dynamicNameForCurrentBreadcrumb
          ? this.getNewNameForCurrentBreadcrumb(
              breadcrumb,
              dynamicNameForCurrentBreadcrumb
            )
          : breadcrumb
      })
    },
    getNewNameForCurrentBreadcrumb(
      breadcrumb,
      dynamicNameForCurrentBreadcrumb
    ) {
      return {
        ...breadcrumb,
        name: dynamicNameForCurrentBreadcrumb
      }
    },
    /**
     * Some dynamic route names might include optional parameters marked with a "?" symbol at the end.
     * It causes a route name mismatching when parsed in breadcrumbs middleware.
     * Example: Article page has the following name: "category-company-article", whereas breadcrumbs treat it
     * as "category-company-article?" which will result in undesirable inequality
     */
    getRouteNameWithoutOptionalSymbol(routeName) {
      return routeName.replace(/\?/, '')
    }
  }
}
</script>

<style lang="scss" scoped>
.breadcrumbs__list-and-slot-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 10px;

  .breadcrumbs__slot-wrapper {
    margin-left: auto;
    flex-shrink: 0;
  }

  .breadcrumbs {
    &__list {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
    }

    &__item {
      & > a {
        color: #4a4a4a;

        &:hover {
          color: $c--main;
        }
      }

      &-link,
      &-last {
        display: inline-block;
      }

      &-last {
        color: #4a4a4a;
        user-select: none;
      }

      &.text-caption,
      &.text-caption > * {
        @include mobile {
          font-size: 7px; // ToDo: check on build/start, not dev
        }
      }
    }

    &__divider {
      color: $c--main;
      padding: 0 7px;
    }
  }
}
</style>
